import { StripeError, StripePaymentMethod } from 'ha/modules/StripeCheckout';
import { MakeCall } from 'ha/services/apiV2/createMakeCall';
import { Currency } from 'ha/constants/Currencies';

export type PaymentRequestStatus = 'cancelled' | 'paid' | 'unpaid' | 'overdue';
export type PaymentRequestFilterStatus = 'all' | PaymentRequestStatus;

export type PaymentRequestUserType = 'author' | 'receiver';

export interface PaymentIntentServerResponse {
  error: StripeError;
  requires_action: boolean;
  payment_intent_client_secret?: string;
}

export interface PaymentRequest {
  handle: string;
  sender: string;
  senderAvatarURL: string;
  amount: string;
  cancelledAt?: string;
  currency: Currency;
  title: string;
  description?: string;
  createdAt: string;
  paidAt?: string;
  platformUserID: number;
  conversation_id?: number;
  tenant_id?: number;
  tenant_uuid?: string;
  dueDate?: string;
  state: PaymentRequestStatus;
  receiptURL?: string;
}

export interface PaymentRequestsResponse {
  handle: string;
  platformUserID: number;
  conversation_id?: number;
  tenant_id?: number;
  tenant_uuid?: string;
}

export interface PayReqCommonParams {
  amount: string;
  currency: Currency;
  title: string;
  description?: string;
  due_date?: string;
}

export interface PayReqConversationParams {
  conversation_id: number;
  tenant_id: number;
  tenant_uuid: string;
}

export type PayReqWithConversationParams = PayReqCommonParams &
  PayReqConversationParams;

export interface PaymentRequestsResponse {
  data: PaymentRequest[];
  hasMore: boolean;
  total_count: number;
}

export interface PayoutsPaymentRequestsFilters {
  limit?: number;
  page: number;
  status?: PaymentRequestFilterStatus;
}

export interface StripeToken {
  card?: {
    brand: string;
    country: string;
    cvc_check?: string;
    exp_month: number;
    exp_year: number;
    funding: string;
    id: string;
    last4: string;
  };
  client_ip: string;
  created: number;
  id: string;
  livemode: boolean;
  object: string;
  type: string;
  used: boolean;
}

export type IdealSourceStatus =
  | 'source.chargeable'
  | 'source.failed'
  | 'source.canceled'
  | 'custom.created'
  | 'custom.succeeded'
  | 'custom.failed'
  | 'charge.succeeded';

interface IdealSource {
  id: string;
  client_secret: string;
  amount: number;
  currency: string; // 'eur'
  statement_descriptor?: string;
  flow: 'redirect';
  object: 'source';
  livemode: boolean; // should be `true` on production
  bank_info: {
    bank?: string | null;
    bic?: string | null;
    iban_last4?: string | null;
    statement_descriptor?: string | null;
  };
}

interface StripeIdealConfirmation {
  source: string;
  client_secret: string;
}

export const getPaymentRequests = (makeCall: MakeCall) => (filters?: {
  limit?: number;
  offset?: number;
  state?: PaymentRequestStatus;
  user_type?: PaymentRequestUserType;
}) =>
  makeCall<PaymentRequestsResponse>('GET', '/api/payments/requests/me/v1', {
    query: {
      limit: 20,
      user_type: 'author',
      ...filters,
    },
  });

export const getPaymentRequestByHandle = (makeCall: MakeCall) => (
  handle: string,
) => makeCall<PaymentRequest>('GET', `/api/payments/requests/v1/${handle}`);

export const addAnonymousPaymentRequest = (makeCall: MakeCall) => (
  params: PayReqCommonParams,
) =>
  makeCall<PaymentRequestsResponse>('POST', '/api/payments/requests/v1', {
    data: params,
  });

export const addConversationPaymentRequest = (makeCall: MakeCall) => (
  params: PayReqWithConversationParams,
) =>
  makeCall<PaymentRequestsResponse>('POST', '/api/payments/requests/v1', {
    data: params,
  });

export const cancelPaymentRequest = (makeCall: MakeCall) => (handle: string) =>
  makeCall<{}>('DELETE', `/api/payments/requests/v1/${handle}`);

export const payPaymentRequest = (makeCall: MakeCall) => (
  handle: string,
  payload: StripeToken,
) =>
  makeCall<PaymentRequest>('PATCH', `/api/payments/requests/v1/${handle}`, {
    data: payload,
  });

export const submitIdealSource = (makeCall: MakeCall) => (
  handle: string,
  payload: IdealSource,
) =>
  makeCall<{ success: boolean }>(
    'PATCH',
    `/api/payments/requests/v2/${handle}/ideal`,
    { data: payload },
  );

export const checkIdealStatus = (makeCall: MakeCall) => (
  handle: string,
  { source, client_secret }: StripeIdealConfirmation,
) =>
  makeCall<{ status: IdealSourceStatus }>(
    'GET',
    `/api/payments/requests/v2/${handle}/ideal/source/${source}/secret/${client_secret}`,
  );

export const payPaymentRequestV2 = (makeCall: MakeCall) => (
  handle: string,
  paymentMethod: Pick<StripePaymentMethod, 'id' | 'card' | 'livemode'>,
) =>
  makeCall<PaymentIntentServerResponse>(
    'PATCH',
    `/api/payments/requests/v2/${handle}`,
    {
      data: {
        card: paymentMethod.card,
        livemode: paymentMethod.livemode,
        payment_method_id: paymentMethod.id,
      },
    },
  );

export const payPaymentRequestV2Confirm = (makeCall: MakeCall) => (
  handle: string,
  paymentIntentId: string,
) =>
  makeCall<{ success: boolean }>(
    'PATCH',
    `/api/payments/requests/v2/${handle}/confirm`,
    { data: { payment_intent_id: paymentIntentId } },
  );

export type GetPaymentRequests = ReturnType<typeof getPaymentRequests>;
export type GetPaymentRequestByHandle = ReturnType<
  typeof getPaymentRequestByHandle
>;
export type AddAnonymousPaymentRequest = ReturnType<
  typeof addAnonymousPaymentRequest
>;
export type AddConversationPaymentRequest = ReturnType<
  typeof addConversationPaymentRequest
>;

export type PayPaymentRequest = ReturnType<typeof payPaymentRequest>;
export type PayPaymentRequestV2 = ReturnType<typeof payPaymentRequestV2>;
export type CancelPaymentRequest = ReturnType<typeof cancelPaymentRequest>;

export type SubmitIdealSource = ReturnType<typeof submitIdealSource>;
export type CheckIdealStatus = ReturnType<typeof checkIdealStatus>;
